'use client';

import { useRef, useEffect, useState } from 'react';
import { AnimationContainer } from '@/components/ui/animation-container';
import { MagicBadge } from '@/components/ui/magic-badge';
import { Input } from '@/components/ui/input';
import { BentoGrid, BentoCard } from '@/components/ui/bento-grid';
import {
  FileText,
  Languages,
  Shield,
  CheckCircle,
  Users,
  FileCheck,
  Zap,
  Upload,
  Globe,
  Edit3,
  Eye,
  Award,
} from 'lucide-react';
import { TranslationKeys } from '../types';

/** Lightweight viewport-triggered fade-in wrapper (replaces framer-motion) */
function FadeIn({ delay = 0, className, children }: { delay?: number; className?: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '50px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `opacity 0.5s ease-out ${delay}s, transform 0.5s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

export default function FeaturesSection({ t, copy }: { t: TranslationKeys; copy?: import('@/lib/marketing/site-copy').SiteCopy }) {
  const { copyOr } = require('@/lib/marketing/site-copy') as typeof import('@/lib/marketing/site-copy');
  const title = copyOr(copy, 'home.features.title', t.featuresTitle);
  const subtitle = copyOr(copy, 'home.features.subtitle', t.featuresDesc);
  // 6 cards — name/desc/back each, overridable via /admin/copy
  const card1Name = copyOr(copy, 'home.features.card1.name', t.smartOcr);
  const card1Desc = copyOr(copy, 'home.features.card1.desc', t.smartOcrDesc);
  const card1Back = copyOr(copy, 'home.features.card1.back', t.smartOcrBack);
  const card2Name = copyOr(copy, 'home.features.card2.name', t.aiTranslationTitle);
  const card2Desc = copyOr(copy, 'home.features.card2.desc', t.aiTranslationDesc);
  const card2Back = copyOr(copy, 'home.features.card2.back', t.aiTranslationBack);
  const card3Name = copyOr(copy, 'home.features.card3.name', t.teamWorkspaceTitle);
  const card3Desc = copyOr(copy, 'home.features.card3.desc', t.teamWorkspaceDesc);
  const card3Back = copyOr(copy, 'home.features.card3.back', t.teamWorkspaceBack);
  const card4Name = copyOr(copy, 'home.features.card4.name', t.legalCertificates);
  const card4Desc = copyOr(copy, 'home.features.card4.desc', t.legalCertificatesDesc);
  const card4Back = copyOr(copy, 'home.features.card4.back', t.legalCertificatesBack);
  const card5Name = copyOr(copy, 'home.features.card5.name', t.sideBySideTitle);
  const card5Desc = copyOr(copy, 'home.features.card5.desc', t.sideBySideDesc);
  const card5Back = copyOr(copy, 'home.features.card5.back', t.sideBySideDesc);
  const card6Name = copyOr(copy, 'home.features.card6.name', t.formatPreservationTitle);
  const card6Desc = copyOr(copy, 'home.features.card6.desc', t.formatPreservationDesc);
  const card6Back = copyOr(copy, 'home.features.card6.back', t.formatPreservationDesc);
  return (
    <section id="features" className="py-4 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <AnimationContainer delay={0.1}>
          <div className="flex flex-col w-full items-center justify-center py-2">
            <MagicBadge title={t.features} />
            <h2 className="text-center text-2xl md:text-4xl !leading-[1.1] font-bold text-gray-900 mt-4">
              {title}
            </h2>
            <p className="mt-3 text-center text-base text-gray-600 max-w-lg">
              {subtitle}
            </p>
          </div>
        </AnimationContainer>

        <BentoGrid className="py-2">
            {/* Smart OCR Card */}
            <FadeIn delay={0.0} className="col-span-3 lg:col-span-1">
              <BentoCard
                name={card1Name}
                className="h-full"
                background={
                  <div className="absolute top-6 left-1/2 -translate-x-1/2 w-[75%] origin-top transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_0%,#000_100%)] group-hover:scale-105">
                    <div className="border border-purple-200 bg-white p-4 rounded-lg shadow-md shadow-purple-100/50">
                      <div className="text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                        <div className="w-5 h-5 rounded bg-purple-100 flex items-center justify-center">
                          <Upload className="w-3 h-3 text-purple-600" />
                        </div>
                        {t.uploadDocument}
                      </div>
                      <div className="border-2 border-dashed border-purple-300 rounded-lg p-5 text-center bg-purple-50/50">
                        <Upload className="w-7 h-7 text-purple-500 mx-auto mb-1.5" />
                        <span className="text-xs text-purple-400 font-medium">{t.dropFiles}</span>
                      </div>
                      <div className="flex gap-1.5 mt-3 justify-center">
                        <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[9px] font-bold rounded">PDF</span>
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-[9px] font-bold rounded">JPG</span>
                        <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-600 text-[9px] font-bold rounded">DOC</span>
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-600 text-[9px] font-bold rounded">TIFF</span>
                      </div>
                    </div>
                  </div>
                }
                Icon={FileText}
                description={card1Desc}
                href="#"
                cta={t.learnMore}
                backContent={card1Back}
                goBackLabel={t.goBack}
              />
            </FadeIn>

            {/* AI Translation Card */}
            <FadeIn delay={0.1} className="col-span-3 lg:col-span-1">
              <BentoCard
                name={card2Name}
                className="h-full"
                background={
                  <div className="absolute right-10 top-10 w-[70%] border border-purple-200 transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_40%,#000_100%)] group-hover:-translate-x-10 p-4 rounded-lg bg-white shadow-md shadow-purple-100/50">
                    <Input placeholder={t.searchDocuments} className="mb-3 border-purple-200 focus:border-purple-400" />
                    <div className="cursor-pointer text-sm">
                      <div className="px-4 py-2 hover:bg-purple-50 rounded flex justify-between items-center">
                        <span className="text-gray-700">{t.birthCertificate}</span>
                        <span className="text-purple-600 font-semibold bg-purple-50 px-2 py-0.5 rounded-md text-xs">ES → EN</span>
                      </div>
                      <div className="px-4 py-2 hover:bg-purple-50 rounded flex justify-between items-center">
                        <span className="text-gray-700">{t.marriageLicense}</span>
                        <span className="text-purple-600 font-semibold bg-purple-50 px-2 py-0.5 rounded-md text-xs">FR → EN</span>
                      </div>
                      <div className="px-4 py-2 hover:bg-purple-50 rounded flex justify-between items-center">
                        <span className="text-gray-600">{t.diploma}</span>
                        <span className="text-purple-500 font-semibold bg-purple-50 px-2 py-0.5 rounded-md text-xs">DE → EN</span>
                      </div>
                      <div className="px-4 py-2 hover:bg-purple-50 rounded flex justify-between items-center">
                        <span className="text-gray-600">{t.passport}</span>
                        <span className="text-purple-500 font-semibold bg-purple-50 px-2 py-0.5 rounded-md text-xs">PT → EN</span>
                      </div>
                      <div className="px-4 py-2 hover:bg-purple-50 rounded flex justify-between items-center">
                        <span className="text-gray-600">{t.legalContract}</span>
                        <span className="text-purple-500 font-semibold bg-purple-50 px-2 py-0.5 rounded-md text-xs">IT → EN</span>
                      </div>
                    </div>
                  </div>
                }
                Icon={Languages}
                description={card2Desc}
                href="#"
                cta={t.learnMore}
                backContent={card2Back}
                goBackLabel={t.goBack}
              />
            </FadeIn>

            {/* Team Workspace Card */}
            <FadeIn delay={0.2} className="col-span-3 lg:col-span-1">
              <BentoCard
                name={card3Name}
                className="h-full"
                background={
                  <div className="absolute left-1/2 -translate-x-1/2 top-2 h-[200px] w-[85%] transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_10%,#000_100%)] group-hover:scale-105">
                    <div className="relative w-full h-full flex items-center justify-center">
                      <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
                        <line x1="50%" y1="50%" x2="25%" y2="20%" stroke="#c4b5fd" strokeWidth="2" strokeDasharray="6 4" />
                        <line x1="50%" y1="50%" x2="75%" y2="20%" stroke="#c4b5fd" strokeWidth="2" strokeDasharray="6 4" />
                        <line x1="50%" y1="50%" x2="15%" y2="65%" stroke="#c4b5fd" strokeWidth="2" strokeDasharray="6 4" />
                        <line x1="50%" y1="50%" x2="85%" y2="65%" stroke="#c4b5fd" strokeWidth="2" strokeDasharray="6 4" />
                        <line x1="50%" y1="50%" x2="50%" y2="15%" stroke="#c4b5fd" strokeWidth="2" strokeDasharray="6 4" />
                      </svg>
                      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center border-2 border-purple-400 z-10 shadow-lg shadow-purple-200/60">
                        <Globe className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="absolute left-[25%] top-[20%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center border-2 border-blue-300 shadow-md shadow-blue-100/50">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="absolute left-[75%] top-[20%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center border-2 border-green-300 shadow-md shadow-green-100/50">
                        <FileCheck className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="absolute left-[15%] top-[65%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center border-2 border-orange-300 shadow-md shadow-orange-100/50">
                        <Edit3 className="w-5 h-5 text-orange-600" />
                      </div>
                      <div className="absolute left-[85%] top-[65%] -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center border-2 border-pink-300 shadow-md shadow-pink-100/50">
                        <Shield className="w-5 h-5 text-pink-600" />
                      </div>
                      <div className="absolute left-[50%] top-[15%] -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-violet-200 rounded-full flex items-center justify-center border-2 border-violet-400 shadow-md shadow-violet-200/50">
                        <Zap className="w-4 h-4 text-violet-600" />
                      </div>
                    </div>
                  </div>
                }
                Icon={Users}
                description={card3Desc}
                href="#"
                cta={t.learnMore}
                backContent={card3Back}
                goBackLabel={t.goBack}
              />
            </FadeIn>

            {/* Legal Certificates Card */}
            {/* ORIGINAL BACKGROUND (uncomment to revert):
              background={
                <div className="absolute right-0 top-10 origin-top rounded-md border border-gray-200 transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_40%,#000_100%)] group-hover:scale-105 bg-white p-4 shadow-sm">
                  <div className="w-44 bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Award className="w-5 h-5 text-purple-600" />
                      <span className="text-xs font-semibold text-gray-700">{t.certificateOfTranslation}</span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 bg-gray-200 rounded w-full"></div>
                      <div className="h-2 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-2 bg-gray-200 rounded w-5/6"></div>
                    </div>
                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                      <span className="text-[10px] text-gray-400">{t.certifiedTranslation}</span>
                      <CheckCircle className="w-4 h-4 text-purple-600" />
                    </div>
                  </div>
                </div>
              }
            */}
            <FadeIn delay={0.30000000000000004} className="col-span-3 lg:col-span-1">
              <BentoCard
                name={card4Name}
                className="h-full"
                background={
                  <div className="absolute top-4 left-6 right-6 origin-top transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_5%,#000_50%)] group-hover:scale-[1.02]">
                    <div className="rounded-lg border border-purple-200 bg-white shadow-md shadow-purple-100/40 overflow-hidden">
                      {/* Mini app header */}
                      <div className="bg-gradient-to-r from-purple-600 to-purple-500 h-6 flex items-center px-2 gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                        <div className="w-1.5 h-1.5 rounded-full bg-white/50" />
                      </div>
                      {/* Stepper completed */}
                      <div className="bg-purple-50/50 border-b border-purple-100 px-2 py-1 flex items-center gap-1.5 justify-center">
                        {['Upload', 'OCR', 'Translate', 'Export'].map((s, i) => (
                          <div key={i} className="flex items-center gap-0.5">
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500 flex items-center justify-center">
                              <CheckCircle className="w-1.5 h-1.5 text-white" />
                            </div>
                            <span className="text-[5px] text-gray-600 font-medium">{s}</span>
                            {i < 3 && <div className="w-3 h-px bg-green-400 mx-0.5" />}
                          </div>
                        ))}
                      </div>
                      {/* Certificate preview */}
                      <div className="p-3 flex items-center justify-center bg-gradient-to-b from-white to-purple-50/30">
                        <div className="w-full max-w-[140px] bg-white rounded-lg border border-purple-200 p-3 shadow-sm">
                          <div className="flex items-center justify-center gap-1 mb-2">
                            <Award className="w-3.5 h-3.5 text-purple-600" />
                            <span className="text-[7px] font-bold text-gray-800">{t.certificateOfTranslation}</span>
                          </div>
                          <div className="space-y-1 mb-2">
                            <div className="h-1 bg-purple-200 rounded w-full" />
                            <div className="h-1 bg-purple-100 rounded w-3/4 mx-auto" />
                            <div className="h-1 bg-purple-100 rounded w-5/6 mx-auto" />
                          </div>
                          <div className="flex items-center justify-center gap-1 pt-2 border-t border-purple-100">
                            <CheckCircle className="w-2.5 h-2.5 text-green-500" />
                            <span className="text-[6px] text-green-600 font-bold">Certified</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                Icon={Shield}
                description={card4Desc}
                href="#"
                cta={t.learnMore}
                backContent={card4Back}
                goBackLabel={t.goBack}
              />
            </FadeIn>

            {/* Side-by-Side Editor Card */}
            <FadeIn delay={0.4} className="col-span-3 lg:col-span-1">
              <BentoCard
                name={card5Name}
                className="h-full"
                background={
                  <div className="absolute top-4 left-4 right-4 origin-top transition-all duration-300 ease-out [mask-image:linear-gradient(to_top,transparent_5%,#000_50%)] group-hover:scale-[1.02]">
                    <div className="rounded-lg border border-purple-200 bg-white shadow-md shadow-purple-100/40 overflow-hidden">
                      {/* Editor toolbar */}
                      <div className="bg-gray-50 border-b border-gray-200 px-3 py-1.5 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-red-400" />
                          <div className="w-2 h-2 rounded-full bg-yellow-400" />
                          <div className="w-2 h-2 rounded-full bg-green-400" />
                        </div>
                        <span className="text-[7px] text-gray-400 font-medium">Side-by-Side Editor</span>
                      </div>
                      {/* Two panels */}
                      <div className="flex divide-x divide-gray-200">
                        <div className="flex-1 p-2.5">
                          <div className="text-[7px] font-bold text-amber-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            Original (ES)
                          </div>
                          <div className="space-y-1.5">
                            <div className="h-1.5 bg-amber-100 rounded w-full" />
                            <div className="h-1.5 bg-amber-50 rounded w-4/5" />
                            <div className="h-1.5 bg-amber-100 rounded w-[90%]" />
                            <div className="h-1.5 bg-amber-50 rounded w-3/4" />
                            <div className="h-1.5 bg-amber-100 rounded w-full" />
                            <div className="h-1.5 bg-amber-50 rounded w-2/3" />
                          </div>
                        </div>
                        <div className="flex-1 p-2.5">
                          <div className="text-[7px] font-bold text-purple-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                            Translation (EN)
                          </div>
                          <div className="space-y-1.5">
                            <div className="h-1.5 bg-purple-100 rounded w-full" />
                            <div className="h-1.5 bg-purple-50 rounded w-3/4" />
                            <div className="h-1.5 bg-purple-100 rounded w-[85%]" />
                            <div className="h-1.5 bg-purple-50 rounded w-full" />
                            <div className="h-1.5 bg-purple-100 rounded w-4/5" />
                            <div className="h-1.5 bg-purple-50 rounded w-[70%]" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                Icon={Eye}
                description={card5Desc}
                href="#"
                cta={t.learnMore}
                backContent={card5Back}
                goBackLabel={t.goBack}
              />
            </FadeIn>

            {/* Format Preservation Card */}
            <FadeIn delay={0.5} className="col-span-3 lg:col-span-1">
              <BentoCard
                name={card6Name}
                className="h-full"
                background={
                  <div className="absolute inset-0 rounded-t-xl overflow-hidden transition-all duration-500 ease-out">
                    {/* Light subtle background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50/40 via-white to-blue-50/30" />
                    {/* 3D Document stack */}
                    <div className="absolute inset-0 flex items-center justify-center" style={{ perspective: '800px' }}>
                      {/* Original document (left, tilted) */}
                      <div className="absolute w-[38%] h-[78%] rounded-lg bg-white shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-gray-200" style={{ transform: 'rotateY(18deg) rotateX(3deg) translateX(-35px) translateZ(-20px)' }}>
                        <div className="bg-gray-100 rounded-t-lg px-2 py-0.5 border-b border-gray-200">
                          <span className="text-[5px] font-semibold text-gray-500 uppercase tracking-wider">Original</span>
                        </div>
                        <div className="p-2">
                          <div className="flex items-start gap-1.5 mb-1.5">
                            <div className="w-6 h-6 bg-blue-100 rounded border border-blue-200 flex items-center justify-center shrink-0">
                              <span className="text-[3px] font-bold text-blue-500">LOGO</span>
                            </div>
                            <div className="flex-1 space-y-[3px]">
                              <div className="h-[2px] bg-gray-200 rounded w-full" />
                              <div className="h-[2px] bg-gray-200 rounded w-3/4" />
                            </div>
                          </div>
                          <div className="space-y-[3px] mb-1.5">
                            <div className="h-[2px] bg-gray-200 rounded w-full" />
                            <div className="h-[2px] bg-gray-200 rounded w-4/5" />
                            <div className="h-[2px] bg-gray-200 rounded w-[90%]" />
                          </div>
                          {/* Table */}
                          <div className="border border-gray-200 rounded mb-1.5">
                            <div className="flex border-b border-gray-200">
                              <div className="flex-1 h-2 bg-gray-100 border-r border-gray-200" />
                              <div className="flex-1 h-2 bg-gray-100" />
                            </div>
                            <div className="flex border-b border-gray-200">
                              <div className="flex-1 h-2 border-r border-gray-200 p-[1px]"><div className="h-full bg-gray-200 rounded w-3/4" /></div>
                              <div className="flex-1 h-2 p-[1px]"><div className="h-full bg-gray-200 rounded w-2/3" /></div>
                            </div>
                            <div className="flex">
                              <div className="flex-1 h-2 border-r border-gray-200 p-[1px]"><div className="h-full bg-gray-200 rounded w-4/5" /></div>
                              <div className="flex-1 h-2 p-[1px]"><div className="h-full bg-gray-200 rounded w-1/2" /></div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between px-0.5">
                            <div className="w-5 h-5 bg-green-50 rounded-full border border-green-200 flex items-center justify-center">
                              <span className="text-[2.5px] font-bold text-green-500">SEAL</span>
                            </div>
                            <div className="w-4 h-4 bg-gray-50 rounded border border-gray-200 flex items-center justify-center">
                              <span className="text-[2.5px] font-bold text-gray-400">QR</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Arrow / connection indicator */}
                      <div className="absolute z-10 flex flex-col items-center gap-0.5" style={{ transform: 'translateY(-5px)' }}>
                        <div className="w-5 h-5 rounded-full bg-purple-100 border border-purple-200 flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-purple-500" />
                        </div>
                      </div>
                      {/* Translated document (right, tilted opposite) */}
                      <div className="absolute w-[38%] h-[78%] rounded-lg bg-white shadow-[0_8px_30px_rgba(139,92,246,0.1)] border border-purple-200" style={{ transform: 'rotateY(-18deg) rotateX(3deg) translateX(35px) translateZ(-20px)' }}>
                        <div className="bg-purple-600 rounded-t-lg px-2 py-0.5 flex items-center justify-between">
                          <span className="text-[5px] font-bold text-white uppercase tracking-wider">Translation</span>
                          <CheckCircle className="w-2 h-2 text-green-300" />
                        </div>
                        <div className="p-2">
                          <div className="flex items-start gap-1.5 mb-1.5">
                            <div className="w-6 h-6 bg-blue-100 rounded border border-blue-200 flex items-center justify-center shrink-0">
                              <span className="text-[3px] font-bold text-blue-500">LOGO</span>
                            </div>
                            <div className="flex-1 space-y-[3px]">
                              <div className="h-[2px] bg-purple-200 rounded w-full" />
                              <div className="h-[2px] bg-purple-200 rounded w-3/4" />
                            </div>
                          </div>
                          <div className="space-y-[3px] mb-1.5">
                            <div className="h-[2px] bg-purple-200 rounded w-full" />
                            <div className="h-[2px] bg-gray-200 rounded w-4/5" />
                            <div className="h-[2px] bg-purple-200 rounded w-[90%]" />
                          </div>
                          {/* Table preserved */}
                          <div className="border border-purple-200 rounded mb-1.5">
                            <div className="flex border-b border-purple-200">
                              <div className="flex-1 h-2 bg-purple-50 border-r border-purple-200" />
                              <div className="flex-1 h-2 bg-purple-50" />
                            </div>
                            <div className="flex border-b border-purple-200">
                              <div className="flex-1 h-2 border-r border-purple-200 p-[1px]"><div className="h-full bg-purple-200 rounded w-3/4" /></div>
                              <div className="flex-1 h-2 p-[1px]"><div className="h-full bg-purple-200 rounded w-2/3" /></div>
                            </div>
                            <div className="flex">
                              <div className="flex-1 h-2 border-r border-purple-200 p-[1px]"><div className="h-full bg-purple-200 rounded w-4/5" /></div>
                              <div className="flex-1 h-2 p-[1px]"><div className="h-full bg-purple-200 rounded w-1/2" /></div>
                            </div>
                          </div>
                          <div className="flex items-center justify-between px-0.5">
                            <div className="w-5 h-5 bg-green-50 rounded-full border border-green-200 flex items-center justify-center">
                              <span className="text-[2.5px] font-bold text-green-500">SEAL</span>
                            </div>
                            <div className="w-4 h-4 bg-gray-50 rounded border border-gray-200 flex items-center justify-center">
                              <span className="text-[2.5px] font-bold text-gray-400">QR</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                }
                Icon={FileCheck}
                description={card6Desc}
                href="#"
                cta={t.learnMore}
                backContent={card6Back}
                goBackLabel={t.goBack}
              />
            </FadeIn>
          </BentoGrid>
      </div>
    </section>
  );
}
